import { Button, Checkbox, Gap, Drawer, P, SvgIcon } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { CampaignExitIntentAreaSelectorModal } from '../CampaignExitIntentAreaSelectorModal';
import { TriggerType } from 'sections/onsite-campaigns/types';
import { useTrigger } from '../hooks/useTrigger';
import { SegmentationConfiguration } from './components/SegmentationConfiguration';
import { InactivityConfiguration } from './components/InactivityConfiguration';
import { ExitIntentConfiguration } from './components/ExitIntentConfigration';
import { PageLoadConfiguration } from '../components/PageLoadConfiguration';
import { OnClickConfiguration } from '../components/OnClickConfiguration';
import { useCanvas } from '../../../Canvas/hooks/useCanvas';
import { FrequencyConfiguration } from './components/FrequencyConfiguration';
import styled from 'styled-components';
import { TriggerEvent } from './components/TriggerEvent';
import { useFeatureFlags } from 'core/app/AppContext';
import { validateOnClickSelector } from '../utils/validateOnClickSelector';

const SegmentationCardHeader = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.designToken.spacing3};
  align-items: center;
`;

const Title = styled.div`
  margin-left: ${({ theme }) => theme.designToken.spacing2};
  font: ${({ theme }) => theme.designToken.body1Semibold};
`;

const SpacingDiv = styled.div`
  margin-bottom: ${({ theme }) => theme.designToken.spacing6};
`;

const CampaignTriggersHeader = styled.div`
  display: flex;
  align-items: center;
`;

const CampaignTriggersTitle = styled(P)`
  margin: 0 0 0 ${({ theme }) => theme.designToken.spacing2};
  color: var(--color-neutral-900, #1d2226);
  font-family: Whitney;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
`;

const FrequencyDescription = styled(P)`
  margin: ${({ theme }) => theme.designToken.spacing2} 0;
`;

const _triggerTypes = Object.values(TriggerType).filter(v => isNaN(Number(v)));

const getAvailableTriggerList = (newTriggersEnabled: boolean) => {
  const triggerTypes = newTriggersEnabled
    ? _triggerTypes
    : _triggerTypes.filter(type => type !== TriggerType.OnClick && type !== TriggerType.PageLoad);

  return triggerTypes.map(triggerType => ({
    value: triggerType,
    label: triggerType.toString(),
  }));
};

const RenderTriggerConfig = ({
  triggerType,
  setExitIntentModalIsOpen,
  onClickSelector,
  selectorError,
  handleSelectorChange,
  overrideFreqCaps,
  handleOverrideFreqCapsChange,
}: {
  triggerType: TriggerType;
  setExitIntentModalIsOpen: (val: boolean) => void;
  onClickSelector: string;
  selectorError: string;
  handleSelectorChange: (value: string) => void;
  overrideFreqCaps: boolean;
  handleOverrideFreqCapsChange: (overrideFreqCaps: boolean) => void;
}) => {
  const triggers = useMemo(
    () => ({
      [TriggerType.ExitIntent]: <ExitIntentConfiguration setExitIntentModalIsOpen={setExitIntentModalIsOpen} />,
      [TriggerType.Inactivity]: <InactivityConfiguration />,
      [TriggerType.PageLoad]: <PageLoadConfiguration />,
      [TriggerType.OnClick]: (
        <OnClickConfiguration
          value={onClickSelector}
          onChange={handleSelectorChange}
          overrideFreqCaps={overrideFreqCaps}
          onOverrideFreqCapsChange={handleOverrideFreqCapsChange}
          validationState={selectorError ? 'error' : 'complete'}
          validationMessage={selectorError}
        />
      ),
    }),
    [
      setExitIntentModalIsOpen,
      onClickSelector,
      selectorError,
      handleSelectorChange,
      overrideFreqCaps,
      handleOverrideFreqCapsChange,
    ],
  );

  return (
    <>
      <div>{triggers[triggerType]}</div>
    </>
  );
};

export interface TriggerDrawerProps {
  open: boolean;
  closeDrawer: () => void;
}

export function TriggerDrawer({ open, closeDrawer }: TriggerDrawerProps) {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();

  const { hideIntroVariant } = useCanvas();
  const { selectedTriggers, removeTriggerType, addTriggerType, onClick, setOnClick } = useTrigger();

  const [exitIntentModalIsOpen, setExitIntentModalIsOpen] = useState(false);
  const [selectorError, setSelectorError] = useState('');

  const newTriggersEnabled = flagEnabled('ld-new-onsite-triggers');
  const availableTriggerList = getAvailableTriggerList(newTriggersEnabled);

  const onClickSelector = onClick?.selector || '';

  useEffect(() => {
    if (onClickSelector) {
      const error = validateOnClickSelector(onClickSelector, t('onsite.campaignOnClickTrigger.validationError'));
      setSelectorError(error);
    } else {
      setSelectorError('');
    }
  }, [onClickSelector, t]);

  const closeTriggerDrawer = () => {
    closeDrawer();
    hideIntroVariant();
  };

  const handleSelectorChange = (value: string) => {
    const error = validateOnClickSelector(value, t('onsite.campaignOnClickTrigger.validationError'));
    setSelectorError(error);
    setOnClick({
      selector: value,
      overrideFreqCaps: onClick?.overrideFreqCaps ?? true,
    });
  };

  const handleOverrideFreqCapsChange = (overrideFreqCaps: boolean) => {
    setOnClick({
      selector: onClick?.selector || '',
      overrideFreqCaps,
    });
  };

  return (
    <Drawer
      header={t('onsite.editor.workflow.triggerMessage.drawer.title')}
      footer={
        <Button
          variation="secondary"
          ariaLabel={t('onsite.editor.workflow.triggerMessage.drawer.closeButtonAria')}
          fullWidth
          onClick={closeTriggerDrawer}
        >
          {t('onsite.editor.workflow.triggerMessage.drawer.closeButton')}
        </Button>
      }
      onClose={closeTriggerDrawer}
      open={open}
    >
      <TriggerEvent />
      <Gap bottom={40} />
      <FrequencyConfiguration />
      <SpacingDiv />
      <CampaignTriggersHeader>
        <SvgIcon name="hand-pointer" size="24" />
        <CampaignTriggersTitle>
          {t('onsite.editor.workflow.triggerMessage.drawer.campaignTriggers')}
        </CampaignTriggersTitle>
      </CampaignTriggersHeader>
      <FrequencyDescription>{t('onsite.editor.workflow.triggerMessage.drawer.frequency')}</FrequencyDescription>
      <Gap bottom={8} />
      {availableTriggerList.map((availableTrigger, availableTriggerKey) => {
        const isPageLoadSelected = selectedTriggers.includes(TriggerType.PageLoad);
        const isCurrentTriggerPageLoad = availableTrigger.value === TriggerType.PageLoad;
        const isDisabled = isPageLoadSelected && !isCurrentTriggerPageLoad;

        return (
          <span key={availableTriggerKey}>
            <Checkbox
              id={availableTrigger.label.toLocaleLowerCase() + ' drawer'}
              label={availableTrigger.label}
              checked={selectedTriggers.includes(availableTrigger.value)}
              disabled={isDisabled}
              onChange={checked => {
                if (checked) {
                  if (isCurrentTriggerPageLoad) {
                    selectedTriggers.forEach(triggerType => {
                      if (triggerType !== TriggerType.PageLoad) {
                        removeTriggerType(triggerType);
                      }
                    });
                  }
                  addTriggerType(availableTrigger.value);
                } else {
                  removeTriggerType(availableTrigger.value);
                }
              }}
            />
            {selectedTriggers.includes(TriggerType.ExitIntent) ? <Gap bottom={16} /> : <Gap bottom={8} />}
            {selectedTriggers.includes(availableTrigger.value) && (
              <>
                <RenderTriggerConfig
                  key={availableTriggerKey}
                  setExitIntentModalIsOpen={setExitIntentModalIsOpen}
                  onClickSelector={onClickSelector}
                  selectorError={selectorError}
                  handleSelectorChange={handleSelectorChange}
                  overrideFreqCaps={onClick?.overrideFreqCaps ?? true}
                  handleOverrideFreqCapsChange={handleOverrideFreqCapsChange}
                  triggerType={availableTrigger.value}
                />
              </>
            )}
          </span>
        );
      })}
      <Gap bottom={24} />
      <SegmentationCardHeader>
        <SvgIcon name="users" size="24" />
        <Title>{t('onsite.editor.workflow.triggerMessage.drawer.segmentation.configTitle')}</Title>
      </SegmentationCardHeader>
      <SegmentationConfiguration />
      <CampaignExitIntentAreaSelectorModal isOpen={exitIntentModalIsOpen} setIsOpen={setExitIntentModalIsOpen} />
    </Drawer>
  );
}
